import { computed, inject, signal } from '@angular/core';
import { RequestDiscussionService } from '../request-discussion/services/request-discussion/request-discussion.service';
import { DiscussionComment } from '../request-discussion/request-discussion.model';
import { Channel } from 'pusher-js';
import {
  TAB_ID_TO_INDEX,
  TAB_IDS,
} from '../request-view-actions/request-view-tabs.constants';
import { Subscription } from 'rxjs';
import { PusherService } from '../../../../common/services/pusher/pusher.service';
import { TenantService } from '../../../../common/services/tenant/tenant.service';
import { REALTIME_CHANNELS } from '../../../../common/constants/realtime-channels.constants';
import { RequestService } from '../../../../common/services/request/request.service';

export class DiscussionBase {
  pusherService = inject(PusherService);
  requestDiscussionService = inject(RequestDiscussionService);
  requestService = inject(RequestService);
  tenantService = inject(TenantService);

  flowId = signal<number>(0);
  showSideDetails = signal<boolean>(false);
  showUnreadMessagesIndicator = signal<boolean>(false);
  activeTabIndex = signal<number>(0); // 0 = Contributors, 1 = Details, 2 = Discussion, 3 = Attachments
  isPusherChannelActive = signal<boolean>(false);

  channelName = signal<string>('');
  channel = signal<Channel | null>(null);
  newComment = signal<DiscussionComment | undefined>(undefined);
  deletedCommentId = signal<number | undefined>(undefined);

  showContributorsTab = computed(() =>
    this.requestService.showContributorsTab()
  );

  subscription = new Subscription();

  readonly DISCUSSION_COMMENT_QUERY_PARAM = 'discussion_opened';
  readonly DISCUSSION_COMMENT_ID_QUERY_PARAM = 'commentId';

  DISCUSSION_TAB_INDEX = computed(() => {
    const showContributorsTab = this.showContributorsTab();
    const discussionTabIndex = TAB_ID_TO_INDEX[TAB_IDS.DISCUSSION];
    return discussionTabIndex - (showContributorsTab ? 0 : 1);
  });

  subscribeToNewComment(): void {
    const channelName = REALTIME_CHANNELS.NEW_COMMENT.replace(
      '{tenant}',
      this.tenantService.getTenant()
    ).replace('{flowId}', this.flowId().toString());
    this.channelName.set(channelName);
    this.channel.set(this.pusherService.subscribe(this.channelName()));
    // Wait for subscription to succeed before binding events
    this.handleNewCommentChannel();
    this.handleDeleteCommentChannel();
  }

  handleNewCommentChannel(): void {
    this.channel()?.bind('comment.created', (data: DiscussionComment) => {
      this.newComment.set({
        ...data,
        reactions: [],
        reactions_count: 0,
        user_reaction: null,
      });
      if (this.activeTabIndex() === this.DISCUSSION_TAB_INDEX()) {
        this.markAsRead();
      } else {
        this.showUnreadMessagesIndicator.set(true);
      }
    });
  }

  handleDeleteCommentChannel(): void {
    this.channel()?.bind('comment.deleted', (data: { comment_id: number }) => {
      this.deletedCommentId.set(data.comment_id);
    });
  }

  markAsRead(): void {
    this.requestDiscussionService
      .markAsRead(this.flowId().toString())
      .subscribe(() => this.showUnreadMessagesIndicator.set(false));
  }

  onTabChanged(tabIndex: number) {
    this.showSideDetails.set(true);
    this.activeTabIndex.set(tabIndex);
  }

  openDetailsSidebar() {
    this.showSideDetails.set(true);
  }

  closeDetailsSidebar() {
    this.showSideDetails.set(false);
  }

  listenToVisibilityChanges(): void {
    this.handleVisibilityChange();
  }

  handleVisibilityChange(): void {
    if (this.isPusherChannelActive()) {
      this.pusherService.unsubscribe(this.channelName());
      this.isPusherChannelActive.set(false);
    } else {
      this.subscribeToNewComment();
    }
  }

  handleOpenedDiscussionFromQueryParams(params: Record<string, any>): void {
    const isDiscussionOpened = params[this.DISCUSSION_COMMENT_QUERY_PARAM];

    if (isDiscussionOpened) {
      this.onTabChanged(this.DISCUSSION_TAB_INDEX());
    }
  }
}
